import { Component } from '@theme/component';

/** How long to stay disarmed after load, so reaching for the nav does not trigger it. */
const ARM_DELAY = 6000;

/**
 * Opens an email-capture dialog once, when a visitor looks like they are leaving.
 *
 * Desktop gets the real signal: the pointer leaving through the top of the viewport, toward
 * the address bar or tab strip. Touch devices have no such gesture, so they fall back to a
 * dwell time or a scroll depth, whichever happens first.
 *
 * A dismissal is remembered, and the dialog never opens over the cart or checkout, where an
 * interruption costs a sale rather than earning an address.
 *
 * @typedef {object} ExitIntentRefs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {Component<ExitIntentRefs>}
 */
class ExitIntentModal extends Component {
  requiredRefs = ['dialog'];

  #timer;
  #armTimer;
  #opened = false;
  #armed = false;

  connectedCallback() {
    super.connectedCallback();

    // In the theme editor the merchant needs to see what they are editing.
    if (this.dataset.editor === 'true') {
      this.#open();
      return;
    }

    if (this.#suppressed()) return;

    // Stay disarmed briefly after load. Someone reaching for the navigation crosses the top
    // of the viewport within the first second or two, and firing then reads as a bug.
    this.#armTimer = setTimeout(() => {
      this.#armed = true;
    }, ARM_DELAY);

    document.addEventListener('mouseout', this.#onMouseOut);
    document.addEventListener('scroll', this.#onScroll, { passive: true });

    const delay = Number(this.dataset.delay ?? 0);
    if (delay > 0) this.#timer = setTimeout(() => this.#open(), delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Whether the dialog should stay shut: already dismissed, or on a page where an
   * interruption would cost more than it earns.
   * @returns {boolean}
   */
  #suppressed() {
    const path = window.location.pathname;
    if (/\/(cart|checkout|challenge|account)/.test(path)) return true;

    try {
      const until = Number(window.localStorage.getItem(this.dataset.storageKey ?? '') || 0);
      return Number.isFinite(until) && until > Date.now();
    } catch {
      // Private browsing can throw on localStorage; showing once is better than crashing.
      return false;
    }
  }

  /**
   * Fires when the pointer leaves the document toward the browser chrome.
   * @param {MouseEvent} event
   */
  #onMouseOut = (event) => {
    if (!this.#armed) return;
    // Only a genuine exit: the pointer left the document entirely, crossing the top edge.
    // `clientY > 0` still covers a pointer travelling up toward the navigation.
    if (event.relatedTarget || event.clientY > 0) return;
    this.#open();
  };

  #onScroll = () => {
    if (!this.#armed) return;
    const depth = Number(this.dataset.scrollDepth ?? 0);
    if (depth <= 0) return;

    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    if ((window.scrollY / scrollable) * 100 >= depth) this.#open();
  };

  #open() {
    if (this.#opened) return;
    this.#opened = true;
    this.#teardown();

    const { dialog } = this.refs;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');

    // Closing by Escape or backdrop counts as a dismissal.
    dialog.addEventListener('close', () => this.#remember(), { once: true });
  }

  close() {
    this.refs.dialog.close();
  }

  dismiss() {
    this.#remember();
    this.close();
  }

  #remember() {
    const days = Number(this.dataset.rememberDays ?? 30);
    try {
      window.localStorage.setItem(
        this.dataset.storageKey ?? '',
        String(Date.now() + days * 24 * 60 * 60 * 1000)
      );
    } catch {
      // Nothing to do — the visitor simply sees it again next session.
    }
  }

  #teardown() {
    document.removeEventListener('mouseout', this.#onMouseOut);
    document.removeEventListener('scroll', this.#onScroll);
    if (this.#timer) clearTimeout(this.#timer);
    if (this.#armTimer) clearTimeout(this.#armTimer);
  }
}

if (!customElements.get('exit-intent-modal')) {
  customElements.define('exit-intent-modal', ExitIntentModal);
}
